import { ClearOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Layout, Radio, Select, Space, Spin, Typography } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import LayoutHeader from '../../../components/LayoutHeader';
import WebRequestConsoleStore from '../stores/WebRequestConsoleStore';
import type { WebRequestConsoleMethod } from '../types';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

type Props = {
    store: WebRequestConsoleStore;
};

const httpMethods: WebRequestConsoleMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

const preStyle: React.CSSProperties = {
    background: '#f6f8fa',
    border: '1px solid #e8e8e8',
    borderRadius: 4,
    margin: 0,
    maxHeight: 320,
    overflow: 'auto',
    padding: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
};

const WebRequestConsolePanel: React.FC<Props> = ({ store }) => {
    const renderResponse = () => {
        if (!store.result) {
            return null;
        }

        const statusText = store.result.statusCode
            ? `${store.result.statusCode} ${store.result.statusText || ''}`.trim()
            : 'Request failed';

        return (
            <Space direction="vertical" size="middle" style={{ marginTop: 16, width: '100%' }}>
                <Space size="middle" wrap>
                    <Text strong>Status: {statusText}</Text>
                    <Text type="secondary">Origin: {store.result.origin}</Text>
                    <Text type="secondary">({store.result.executionTimeMs}ms)</Text>
                </Space>

                <div>
                    <div style={{ marginBottom: 4 }}>
                        <Text strong>Response Headers</Text>
                    </div>
                    <pre data-id="web-request-console-response-headers" style={preStyle}>
                        {store.formattedResponseHeaders || '{}'}
                    </pre>
                </div>

                <div>
                    <div style={{ marginBottom: 4 }}>
                        <Text strong>Response Body</Text>
                    </div>
                    <pre data-id="web-request-console-response-body" style={preStyle}>
                        {store.formattedResponseBody}
                    </pre>
                </div>

                {store.result.errorDetails ? (
                    <Alert
                        data-id="web-request-console-error-details"
                        message="Request Error"
                        description={store.result.errorDetails}
                        type="error"
                        showIcon
                    />
                ) : null}
            </Space>
        );
    };

    return (
        <Layout style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                title="Web Request Console"
                helpMessage={
                    'Execute lightweight HTTP requests from the browser or from ManageService infrastructure. ' +
                    'Server-side requests are restricted by backend diagnostics policy.'
                }
            />
            <Layout>
                <Content
                    style={{
                        maxHeight: 'calc(100vh - 70px)',
                        overflowY: 'auto',
                        padding: '16px 24px'
                    }}
                >
                    <Spin spinning={store.isExecuting} tip="Executing request...">
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Space size="middle" wrap>
                                <div>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong>Method</Text>
                                    </div>
                                    <Select
                                        data-id="web-request-console-method-select"
                                        value={store.method}
                                        onChange={(value: WebRequestConsoleMethod) => store.setMethod(value)}
                                        style={{ width: 120 }}
                                    >
                                        {httpMethods.map(method => (
                                            <Option key={method} value={method}>
                                                {method}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                <div style={{ flex: 1, minWidth: 360 }}>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong>URL</Text>
                                    </div>
                                    <Input
                                        data-id="web-request-console-url-input"
                                        placeholder="https://example.com/api"
                                        value={store.url}
                                        onChange={e => store.setUrl(e.target.value)}
                                    />
                                </div>

                                <div>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text strong>Origin</Text>
                                    </div>
                                    <Radio.Group
                                        data-id="web-request-console-origin-radio"
                                        value={store.origin}
                                        onChange={e => store.setOrigin(e.target.value)}
                                    >
                                        <Radio.Button value="client">Client side</Radio.Button>
                                        <Radio.Button value="server">Server side</Radio.Button>
                                    </Radio.Group>
                                </div>
                            </Space>

                            <div>
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>Headers (JSON)</Text>
                                </div>
                                <TextArea
                                    data-id="web-request-console-headers-input"
                                    value={store.headers}
                                    onChange={e => store.setHeaders(e.target.value)}
                                    rows={4}
                                    style={{ fontFamily: 'monospace' }}
                                />
                                {store.validationError ? <Text type="danger">{store.validationError}</Text> : null}
                            </div>

                            <div>
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>Body</Text>
                                </div>
                                <TextArea
                                    data-id="web-request-console-body-input"
                                    value={store.body}
                                    onChange={e => store.setBody(e.target.value)}
                                    rows={7}
                                    disabled={!store.canHaveBody}
                                    style={{ fontFamily: 'monospace' }}
                                />
                            </div>

                            <Space>
                                <Button
                                    data-id="web-request-console-execute-button"
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => store.executeRequest()}
                                    disabled={!store.canExecute}
                                    loading={store.isExecuting}
                                >
                                    Execute Request
                                </Button>
                                <Button
                                    data-id="web-request-console-clear-button"
                                    icon={<ClearOutlined />}
                                    onClick={() => store.clearResults()}
                                    disabled={!store.result && !store.validationError}
                                >
                                    Clear Results
                                </Button>
                            </Space>
                        </Space>

                        {renderResponse()}
                    </Spin>
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(WebRequestConsolePanel);
